import { create } from "zustand";
import type { Bbox } from "../utils/bbox";

interface MapBboxStore {
  /** Bounding box of the current viewport, `null` until it is known. */
  bbox: Bbox | null;
  /**
   * Whether the viewport question has been answered — either with a box, or
   * with "no map will supply one".
   *
   * Queries that scope to the viewport wait on this rather than on `bbox` being
   * non-null, because those are different states: "not measured yet" must hold
   * the query back, while "measured, and there is no map" must let it through
   * unscoped. Without the distinction the first request goes out before the map
   * reports its bounds and returns the whole dataset.
   */
  isResolved: boolean;
  setBbox: (bbox: Bbox) => void;
  /**
   * Stop waiting for a viewport that will never arrive — no Mapbox token, so no
   * map is ever constructed. The list still loads, unscoped.
   */
  resolveWithoutMap: () => void;
}

/**
 * Viewport bounding box, used to restrict items and suggestions to what is
 * visible on the map. Written by `useTrackMapBbox`.
 *
 * Deliberately not in the URL: it changes on every pan and would flood the
 * history. The map's own camera position *is* URL-backed (the `map` hash param),
 * which is what makes a shared link reproduce the view.
 */
export const useMapBbox = create<MapBboxStore>()((set) => ({
  bbox: null,
  isResolved: false,
  setBbox: (bbox) => set({ bbox, isResolved: true }),
  resolveWithoutMap: () => set({ bbox: null, isResolved: true }),
}));
